import { useFormikContext } from 'formik';
import React from 'react';
import styled from 'styled-components';
import { Checkbox, Icon, Radio, RadioGroup, Tooltip } from '~/components';
import { colors, weights } from '~/styles';

export const NO_CHANGE = 'no_change';
export const CLEAR = 'clear';
export const SET = 'set';

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LabelWrapper = styled.div`
  min-width: 10rem;
  padding-top: 0.25rem;
`;

const LabelText = styled.span`
  font-weight: ${weights.medium};
  color: ${colors.grey100};
`;

const RadioWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 0.25rem;
  min-width: 10rem;
`;

const ControlWrapper = styled.div`
  flex: 1;
  display: flex;
  gap: 1rem;
`;

export default function BulkActionEditControl({
  label,
  actionName,
  valueName,
  clearLabel = 'Clear',
  emptyValue = null,
  allowClear = true,
  multiSelect = false,
  children,
}) {
  const { values, setValues, errors, touched, setFieldTouched } = useFormikContext();

  const action = values[actionName];
  const value = values[valueName];
  const isEnabled = action !== NO_CHANGE;
  const selectorDisabled = action !== SET;

  const handleCheckboxChange = (e) => {
    if (e.target.checked) {
      setValues({ ...values, [actionName]: SET }, true);
    } else {
      setValues({ ...values, [actionName]: NO_CHANGE, [valueName]: emptyValue }, true);
    }
  };

  const handleActionChange = (newAction) => {
    const newValues = { ...values, [actionName]: newAction };
    if (newAction !== SET) {
      newValues[valueName] = emptyValue;
    }
    setValues(newValues, true);
  };

  const handleValueChange = (event) => {
    setValues({ ...values, [valueName]: event.target.value }, true);
  };

  const actionError = touched[actionName] && errors[actionName];
  const valueError = touched[valueName] && errors[valueName];
  const error = actionError || valueError;

  const inputProps = {
    name: valueName,
    value,
    disabled: selectorDisabled,
    onChange: handleValueChange,
    onBlur: () => setFieldTouched(valueName, true),
  };

  return (
    <Row>
      <LabelWrapper>
        <Checkbox checked={isEnabled} onChange={handleCheckboxChange} label={<LabelText>{label}</LabelText>} />
      </LabelWrapper>
      <RadioWrapper>
        <RadioGroup value={action} onChange={handleActionChange}>
          <Radio value={CLEAR} label={clearLabel} disabled={!isEnabled || !allowClear} />
          <Radio value={SET} label={multiSelect ? 'Add' : 'Set'} disabled={!isEnabled} />
        </RadioGroup>
      </RadioWrapper>
      <ControlWrapper>
        <div style={{ flex: 1 }}>{children(inputProps)}</div>
        {error && (
          <Tooltip message={error} style={{ fontSize: '1.5rem' }}>
            <Icon icon="exclamation-circle" color={colors.danger} />
          </Tooltip>
        )}
      </ControlWrapper>
    </Row>
  );
}
